import React, { useState, useRef, useEffect, useCallback } from 'react';

const ImageUpload = ({ onImageSelect, currentImage, productType }) => {
  const [preview, setPreview] = useState(currentImage || null);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [imageScale, setImageScale] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullImage, setIsFullImage] = useState(false);
  const [sizeError, setSizeError] = useState('');
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  // Resolução aumentada para manter qualidade (300 DPI para impressão)
  const productDimensions = {
    'BOTTOM': { 
      type: 'circle', 
      width: 120, 
      height: 120,
      diameter: 120,
      displaySize: 200,
      finalWidth: 472,  
      finalHeight: 472,
      label: 'Círculo 4cm de diâmetro'
    },
    'KEYCHAIN': { 
      type: 'rectangle', 
      width: 113.4, 
      height: 151.2, 
      displaySize: 200,
      finalWidth: 354,  // 3cm a 300 DPI
      finalHeight: 472, // 4cm a 300 DPI
      label: 'Retângulo 3x4cm'
    },
    'POLAROID': {
      type: 'rectangle',
      width: 189, 
      height: 302.4, 
      displaySize: 200,
      finalWidth: 590,  // 5cm a 300 DPI
      finalHeight: 945, // 8cm a 300 DPI
      label: 'Retângulo 5x8cm'
    },
    'STICK': {
      type: 'rectangle',
      width: 189, 
      height: 189, 
      displaySize: 200,
      finalWidth: 590,  // 5cm a 300 DPI
      finalHeight: 590, // 5cm a 300 DPI
      label: 'Quadrado 5x5cm'
    },
    'default': {
      type: 'rectangle',
      width: 200,
      height: 200,
      displaySize: 200,
      finalWidth: 600,
      finalHeight: 600,
      label: 'Área padrão'
    }
  };

  const getDimensions = useCallback(() => {
    return productDimensions[productType] || productDimensions['default'];
  }, [productType]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setSizeError('Arquivo muito grande! Máximo 5MB.');
        return;
      }
      setSizeError('');
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        setPreview(dataUrl);
        setImagePosition({ x: 0, y: 0 });
        setImageScale(0.5); // Inicia com 50% para evitar imagens muito grandes
        setImageRotation(0);
        // Padrão: envia a imagem original completa, sem recorte.
        setIsFullImage(true);
        onImageSelect(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (e) => {
    if (!preview || isFullImage) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleTouchStart = (e) => {
    if (!preview || isFullImage) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - imagePosition.x,
      y: touch.clientY - imagePosition.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleRemove = () => {
    setPreview(null);
    setImagePosition({ x: 0, y: 0 });
    setImageScale(0.5);
    setImageRotation(0);
    setIsFullImage(false);
    onImageSelect(null);
  };

  const handleZoomChange = (e) => {
    if (isFullImage) return;
    const value = parseInt(e.target.value);
    setImageScale(value / 100);
  };

  const handleRotateLeft = () => {
    if (isFullImage) return;
    setImageRotation(prev => prev - 15);
  };

  const handleRotateRight = () => {
    if (isFullImage) return;
    setImageRotation(prev => prev + 15);
  };

  const handleToggleCrop = () => {
    if (!preview) return;
    if (isFullImage) {
      // Volta a ajustar: o canvas passa a enviar a versão recortada.
      setIsFullImage(false);
    } else {
      // Volta ao padrão: envia a imagem original, sem zoom/posição/rotação.
      setIsFullImage(true);
      onImageSelect(preview);
    }
  };

  const drawCanvas = useCallback(() => {
    if (!canvasRef.current || !imageRef.current || isFullImage) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { 
      alpha: true,
      desynchronized: false,
      willReadFrequently: false
    });
    
    // Melhorar qualidade de renderização no canvas de preview
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    const img = imageRef.current;
    const dims = getDimensions();
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    let imgWidth = img.naturalWidth * imageScale;
    let imgHeight = img.naturalHeight * imageScale;
    
    ctx.save();
    ctx.translate(canvas.width / 2 + imagePosition.x, canvas.height / 2 + imagePosition.y);
    ctx.rotate((imageRotation * Math.PI) / 180);
    ctx.drawImage(img, -imgWidth / 2, -imgHeight / 2, imgWidth, imgHeight);
    ctx.restore();
    
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.globalCompositeOperation = 'destination-out';
    
    if (dims.type === 'circle') {
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, dims.width / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const x = (canvas.width - dims.width) / 2;
      const y = (canvas.height - dims.height) / 2;
      ctx.fillRect(x, y, dims.width, dims.height);
    }
    ctx.restore();
    
    ctx.strokeStyle = '#3B82F6';
    ctx.lineWidth = 2;
    if (dims.type === 'circle') {
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, dims.width / 2, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      const x = (canvas.width - dims.width) / 2;
      const y = (canvas.height - dims.height) / 2;
      ctx.strokeRect(x, y, dims.width, dims.height);
    }
    
    // Canvas em alta resolução para manter qualidade
    const finalCanvas = document.createElement('canvas');
    const scaleFactor = (dims.finalWidth || dims.width * 3) / dims.width;
    finalCanvas.width = dims.finalWidth || dims.width * 3;
    finalCanvas.height = dims.finalHeight || dims.height * 3;
    const finalCtx = finalCanvas.getContext('2d', { 
      alpha: true,
      desynchronized: false,
      willReadFrequently: false
    });
    
    // Melhorar qualidade de renderização
    finalCtx.imageSmoothingEnabled = true;
    finalCtx.imageSmoothingQuality = 'high';
    
    finalCtx.fillStyle = '#FFFFFF';
    finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
    
    finalCtx.save();
    
    if (dims.type === 'circle') {
      finalCtx.beginPath();
      finalCtx.arc(finalCanvas.width / 2, finalCanvas.height / 2, finalCanvas.width / 2, 0, Math.PI * 2);
      finalCtx.clip();
    }
    const cropX = (canvas.width - dims.width) / 2;
    const cropY = (canvas.height - dims.height) / 2;
    
    const finalImgX = ((canvas.width / 2 + imagePosition.x) - cropX) * scaleFactor;
    const finalImgY = ((canvas.height / 2 + imagePosition.y) - cropY) * scaleFactor;
    
    finalCtx.translate(finalImgX, finalImgY);
    finalCtx.rotate((imageRotation * Math.PI) / 180);
    finalCtx.drawImage(img, -imgWidth * scaleFactor / 2, -imgHeight * scaleFactor / 2, imgWidth * scaleFactor, imgHeight * scaleFactor);
    finalCtx.restore();
    
    // PNG para qualidade máxima sem perda
    const finalData = finalCanvas.toDataURL('image/png');
    onImageSelect(finalData);
  }, [imageScale, imagePosition, imageRotation, onImageSelect, getDimensions, isFullImage]);

  useEffect(() => {
    if (preview && canvasRef.current) {
      const img = new Image();
      
      img.onload = () => {
        imageRef.current = img;
        drawCanvas();
      };
      img.src = preview;
    }
  }, [preview, drawCanvas]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setImagePosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setImagePosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove]);

  const dims = getDimensions();

  return (
    <div className="w-full">
      {!preview ? (
        <div className="w-full">
        <label className="flex flex-col items-center justify-center w-full h-48 sm:h-64 cursor-pointer rounded-card border-2 border-dashed border-cocoa-200 bg-cream-50 transition-colors hover:border-cocoa-400 hover:bg-cream-100">
          <div className="flex flex-col items-center justify-center px-4 pb-5 pt-4 sm:pb-6 sm:pt-5">
            <svg className="mb-3 h-10 w-10 text-cocoa-400 sm:h-12 sm:w-12" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="mb-2 text-center text-xs sm:text-sm text-ink-soft">
              <span className="font-semibold text-ink">Toque para fazer upload</span> ou arraste a imagem
            </p>
            <p className="text-center text-xs text-ink-muted">PNG, JPG ou JPEG (máx. 5MB)</p>
            {productType && dims.label && (
              <div className="mt-3 rounded-full bg-caramel-50 px-3 py-1 text-xs font-semibold text-caramel-700">
                {dims.label}
              </div>
            )}
          </div>
          <input 
            type="file" 
            className="hidden" 
            accept="image/*" 
            onChange={handleImageChange}
          />
        </label>
        {sizeError && (
          <p role="alert" className="mt-2 rounded-xl bg-clay-50 px-3 py-2 text-xs font-medium text-clay-700">
            {sizeError}
          </p>
        )}
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          <div className="relative flex h-[300px] max-h-[400px] items-center justify-center overflow-hidden rounded-card bg-cream-100">
            {isFullImage ? (
              <img 
                src={preview} 
                alt="Imagem completa" 
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <canvas
                ref={canvasRef}
                width={400}
                height={400}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                className={`${isDragging ? 'cursor-grabbing' : 'cursor-grab'} touch-none`}
                style={{ maxWidth: '100%', maxHeight: '100%', touchAction: 'none' }}
              />
            )}
            {isFullImage ? (
              <div className="absolute left-2 top-2 inline-flex items-center gap-1.5 rounded-full bg-sage-600 px-3 py-1.5 text-xs font-bold text-white shadow-card sm:text-sm">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Imagem completa (sem cortes)
              </div>
            ) : (
              <div className="absolute left-2 top-2 rounded-full bg-cocoa-950/70 px-3 py-1 text-xs text-cream-50">
                Arraste para posicionar
              </div>
            )}
          </div>
          
          <div className="space-y-3 sm:space-y-4">
            {/* Alterna entre imagem completa (padrão) e recorte manual */}
            <div className="w-full">
              <button
                onClick={handleToggleCrop}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-cocoa-800 px-4 py-3 text-sm font-semibold text-cream-50 transition-colors hover:bg-cocoa-900"
                aria-label={isFullImage ? 'Ajustar imagem e recortar' : 'Enviar imagem completa'}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.16-5.16a2.25 2.25 0 013.18 0l5.16 5.16M9 9.75h.01M3.75 19.5h16.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v10.5a2.25 2.25 0 002.25 2.25z" />
                </svg>
                <span className="font-medium">
                  {isFullImage ? 'Ajustar imagem (recortar)' : 'Enviar imagem completa'}
                </span>
              </button>
            </div>

            {/* Zoom Control */}
            <div className={`w-full ${isFullImage ? 'pointer-events-none opacity-50' : ''}`}>
              <label className="mb-2 block text-xs font-medium text-ink sm:text-sm">
                Zoom: {(imageScale * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min="5"
                max="300"
                value={imageScale * 100}
                onChange={handleZoomChange}
                disabled={isFullImage}
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-cocoa-100"
                style={{
                  background: `linear-gradient(to right, #3d2e2b 0%, #3d2e2b ${((imageScale * 100 - 5) / 295) * 100}%, #ece7e6 ${((imageScale * 100 - 5) / 295) * 100}%, #ece7e6 100%)`
                }}
              />
              <div className="mt-1 flex justify-between text-xs text-ink-muted">
                <span>5%</span>
                <span>300%</span>
              </div>
            </div>
            
            {/* Rotation and Remove Controls */}
            <div className="flex flex-col gap-2">
              <div className={`flex flex-wrap gap-2 ${isFullImage ? 'pointer-events-none opacity-50' : ''}`}>
                <button
                  onClick={handleRotateLeft}
                  disabled={isFullImage}
                  className="flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full border border-cocoa-300 bg-white px-3 py-2.5 text-xs font-semibold text-cocoa-700 transition-colors hover:bg-cocoa-50"
                  title="Rotacionar para esquerda"
                  aria-label="Rotacionar para esquerda"
                >
                  <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Girar
                </button>
                <div className="flex min-w-[52px] items-center justify-center rounded-full bg-cream-100 px-2 py-2.5 text-xs font-medium text-ink-soft">
                  {imageRotation}°
                </div>
                <button
                  onClick={handleRotateRight}
                  disabled={isFullImage}
                  className="flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full border border-cocoa-300 bg-white px-3 py-2.5 text-xs font-semibold text-cocoa-700 transition-colors hover:bg-cocoa-50"
                  title="Rotacionar para direita"
                  aria-label="Rotacionar para direita"
                >
                  Girar
                  <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" />
                  </svg>
                </button>
              </div>
              
              <button
                onClick={handleRemove}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-clay-50 px-4 py-2.5 text-sm font-semibold text-clay-700 transition-colors hover:bg-clay-100"
                aria-label="Remover imagem"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
